import { Matrix4, Vector3 } from 'three'
import { ImmutableVector3, getFlatShapeNormal } from '@modugen/ui-toolkit'
import { getPolygonNormal, makeZDirectionZPositive } from '../utils'

export const AREA_LOAD_THICKNESS_PER_KN = 0.5
export const AREA_LOAD_TUBE_RADIUS = 0.015
export const AREA_LOAD_LABEL_GAP = 0.01
export const AREA_LOAD_TEXT_SCALE = 0.15

export const toAreaLoadPoints = (
  points: Array<{ x: number; y: number; z: number }>,
): ImmutableVector3[] => points.map(point => new ImmutableVector3(point.x, point.y, point.z))

const MIN_EDGE_LENGTH_SQ = 1e-12

export interface AreaLoadExtrusion {
  extrusionNormal: Vector3
  thickness: number
}

const getRobustPolygonNormal = (points: ImmutableVector3[]): Vector3 => {
  try {
    const normal = getFlatShapeNormal(
      points as [ImmutableVector3, ImmutableVector3, ImmutableVector3, ...ImmutableVector3[]],
    )
    const vector = new Vector3(normal.x, normal.y, normal.z)
    if (vector.lengthSq() > MIN_EDGE_LENGTH_SQ) return vector.normalize()
  } catch {
    // Non-rectangular polygons fall back to the first-three-points normal.
  }

  return getPolygonNormal(points).v.clone().normalize()
}

export const getAreaLoadExtrusion = (
  points: ImmutableVector3[],
  force: Force,
): AreaLoadExtrusion => {
  if (points.length < 3) {
    return { extrusionNormal: new Vector3(0, 0, 1), thickness: 0 }
  }

  // Downward loads are drawn on top of the area, upward loads (e.g. uplift on roof slabs)
  // below it, so the block always sits on the side the load pushes from.
  const upwardNormal = makeZDirectionZPositive(getRobustPolygonNormal(points))
  const extrusionNormal = force.z > 0 ? upwardNormal.negate() : upwardNormal

  return {
    extrusionNormal,
    thickness: Math.abs(force.z / 1000) * AREA_LOAD_THICKNESS_PER_KN,
  }
}

export const getAreaLoadLongestEdgeDirection = (points: ImmutableVector3[]): Vector3 => {
  let best = new Vector3(1, 0, 0)
  let bestLengthSq = 0

  for (let i = 0; i < points.length; i++) {
    const edge = points[(i + 1) % points.length].sub(points[i]).v
    const lengthSq = edge.lengthSq()
    if (lengthSq > bestLengthSq) {
      bestLengthSq = lengthSq
      best = edge
    }
  }

  return bestLengthSq > MIN_EDGE_LENGTH_SQ ? best.normalize() : new Vector3(1, 0, 0)
}

export const getAreaLoadShapeMatrix = (
  points: ImmutableVector3[],
  extrusionNormal: Vector3,
): Matrix4 => {
  const zAxis = extrusionNormal.clone().normalize()
  let xAxis = getAreaLoadLongestEdgeDirection(points)
  xAxis.sub(zAxis.clone().multiplyScalar(xAxis.dot(zAxis)))

  if (xAxis.lengthSq() <= MIN_EDGE_LENGTH_SQ) {
    xAxis = Math.abs(zAxis.z) < 0.9 ? new Vector3(0, 0, 1) : new Vector3(1, 0, 0)
    xAxis.sub(zAxis.clone().multiplyScalar(xAxis.dot(zAxis)))
  }

  xAxis.normalize()
  const yAxis = zAxis.clone().cross(xAxis).normalize()

  const transform = new Matrix4().makeBasis(xAxis, yAxis, zAxis)
  if (points[0]) transform.setPosition(points[0].v.clone())
  return transform
}
